import type { IAccount } from "@/modules/account/common/types";
import type { IPersonalizedExchangeRate } from "@/modules/transaction/common/types";
import { CURRENCY } from "@/shared/common/constants";
import { isEqual, renderSelectedAccount } from "@/shared/helpers";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { useEffect, useMemo, useState } from "react";

interface IUseCurrencyExchangeParams {
  accountsList: IAccount[];
  rateInfo?: IPersonalizedExchangeRate
}

export const useCurrencyExchange = ({ accountsList, rateInfo }: IUseCurrencyExchangeParams) => {
  const { setValue, setError, watch, errors = {}, reset } = useFormProps();
  const [creditAccountList, setCreditAccountList] = useState<IAccount[]>(accountsList);
  const [debitAccountList, setDebitAccountList] = useState<IAccount[]>(accountsList);
  const values = watch?.() || {};

  const creditAccount = useMemo(() => {
    return accountsList?.find(({ number }) => number === values.creditAccount);
  }, [values.creditAccount, accountsList]);

  const debitAccount = useMemo(() => {
    return accountsList?.find(({ number }) => number === values.debitAccount);
  }, [values.debitAccount, accountsList]);

  const creditAccountsFilteredList = useMemo(() =>
    accountsList?.filter((item) => item.currency !== debitAccount?.currency).map(
      (item, index) => ({
        value: item.number,
        label: renderSelectedAccount(item),
        meta: { ...item, index },
      })), [accountsList, debitAccount]);

  const debitAccountsFilteredList = useMemo(() =>
    accountsList?.filter((item) => item.currency !== creditAccount?.currency).map((
      item,
      index
    ) => ({
      value: item.number,
      label: renderSelectedAccount(item),
      meta: { ...item, index },
    })), [accountsList, creditAccount]);

  const bankRateHelperText = useMemo(() => {
    if (debitAccount?.currency && creditAccount?.currency && rateInfo?.currency) {
      return `1 ${rateInfo.currency === creditAccount.currency ?
        CURRENCY[debitAccount?.currency as keyof typeof CURRENCY]?.symbol
        : CURRENCY[creditAccount?.currency as keyof typeof CURRENCY]?.symbol
      } = ${rateInfo.rate} ${CURRENCY[rateInfo?.currency as keyof typeof CURRENCY]?.symbol}`;
    }
    return "";
  }, [rateInfo, debitAccount, creditAccount]);

  useEffect(() => {
    if (!accountsList?.length) return;
    if (!isEqual(creditAccountList, accountsList)) {
      setCreditAccountList(accountsList);
    }
    if (!isEqual(debitAccountList, accountsList)) {
      setDebitAccountList(accountsList);
    }
  }, [accountsList]);

  return {
    watch,
    reset,
    errors,
    values,
    setError,
    setValue,
    debitAccount,
    creditAccount,
    bankRateHelperText,
    creditAccountList,
    debitAccountList,
    setCreditAccountList,
    setDebitAccountList,
    creditAccountsFilteredList,
    debitAccountsFilteredList,
  };
};
